import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";
import { useEffect, useCallback } from "react";

export type Product = {
  id: number;
  name: string;
  code: string | null;
  brand_id: number | null;
  created_at: string;
  updated_at: string;
  // Joined data
  brands?: {
    id: number;
    name: string;
    supplier_id: number;
    suppliers?: {
      id: number;
      name: string;
    } | null;
  } | null;
};

/**
 * Search products by name or code prefix. Returns empty array for empty search string.
 * Used by autocomplete components.
 */
export async function searchProducts(
  searchText: string,
  limit = 10
): Promise<Product[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("products")
    .select("*, brands(id, name, supplier_id, suppliers(id, name))")
    .or(`name.ilike.%${searchText}%,code.ilike.%${searchText}%`)
    .order("name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single product by ID. Used to display the selected product name.
 */
export async function fetchProductById(id: number): Promise<Product | null> {
  const { data, error } = await supabase
    .from("products")
    .select("*, brands(id, name, supplier_id, suppliers(id, name))")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data;
}

export async function fetchProducts(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams
): Promise<FetchResult<Product>> {
  // First, get total count with search filters
  let countQuery = supabase
    .from("products")
    .select("*, brands!inner(supplier_id)", { count: "exact", head: true });

  // Apply text search filter for count (on name or code)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    countQuery = countQuery.or(
      `name.ilike.${searchText},code.ilike.${searchText}`
    );
  }

  // Apply brand_id filter for count
  if (searchParams?.filters?.brand_id) {
    countQuery = countQuery.eq(
      "brand_id",
      searchParams.filters.brand_id as number
    );
  }

  // Apply supplier_id filter for count (via brand)
  if (searchParams?.filters?.supplier_id) {
    countQuery = countQuery.eq(
      "brands.supplier_id",
      searchParams.filters.supplier_id as number
    );
  }

  const { count } = await countQuery;

  // Now get the data with brand and supplier relations
  let query = supabase
    .from("products")
    .select("*, brands!inner(id, name, supplier_id, suppliers(id, name))");

  // Apply sorting (default to name ascending if not specified)
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";
  query = query.order(sortColumn, { ascending: sortAscending });

  // Apply text search filter (on name or code)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    query = query.or(`name.ilike.${searchText},code.ilike.${searchText}`);
  }

  // Apply brand_id filter
  if (searchParams?.filters?.brand_id) {
    query = query.eq("brand_id", searchParams.filters.brand_id as number);
  }

  // Apply supplier_id filter (via brand)
  if (searchParams?.filters?.supplier_id) {
    query = query.eq(
      "brands.supplier_id",
      searchParams.filters.supplier_id as number
    );
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  const items = data ?? [];
  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

const productsAtom = atom<Product[]>([]);

export function useProducts() {
  const [products, setProducts] = useAtom(productsAtom);

  const fetchAllProducts = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("products")
        .select("*, brands(id, name, supplier_id, suppliers(id, name))")
        .order("name", { ascending: true });

      if (error) throw error;
      setProducts(data ?? []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }, [setProducts]);

  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  return [products, fetchAllProducts] as const;
}
